import { useCallback, useEffect, useRef, useState } from 'react';

const POLLING_INTERVAL = 5000;
const MAX_POLLING_ATTEMPTS = 36;

export const useVideoPolling = (videoUrl, isNsfw) => {
  const [isGenerating, setIsGenerating] = useState(true);
  const [isInitialCheckComplete, setIsInitialCheckComplete] = useState(false);
  const [pollingAttempts, setPollingAttempts] = useState(0);
  const videoCheckRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  const checkVideoReadiness = useCallback(() => {
    if (!videoUrl || !videoCheckRef.current || isNsfw) return;

    const testVideo = document.createElement('video');
    testVideo.preload = 'metadata';
    testVideo.muted = true;

    const handleCanPlay = () => {
      setIsGenerating(false);
      setIsInitialCheckComplete(true);
      clearInterval(pollingIntervalRef.current);
      testVideo.removeEventListener('canplaythrough', handleCanPlay);
      testVideo.removeEventListener('error', handleError);
      testVideo.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };

    const handleLoadedMetadata = () => {
      if (testVideo.duration > 0) {
        handleCanPlay();
      }
    };

    const handleError = () => {
      setPollingAttempts(prev => prev + 1);
      setIsInitialCheckComplete(true);
      testVideo.removeEventListener('canplaythrough', handleCanPlay);
      testVideo.removeEventListener('error', handleError);
      testVideo.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };

    testVideo.addEventListener('canplaythrough', handleCanPlay);
    testVideo.addEventListener('loadedmetadata', handleLoadedMetadata);
    testVideo.addEventListener('error', handleError);

    const urlWithCacheBuster = `${videoUrl}?t=${Date.now()}`;
    testVideo.src = urlWithCacheBuster;
    testVideo.load();
  }, [videoUrl, isNsfw]);

  useEffect(() => {
    if (isNsfw) {
      setIsGenerating(false);
      setIsInitialCheckComplete(true);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    }
  }, [isNsfw]);

  useEffect(() => {
    if (!videoUrl || isNsfw) return;

    checkVideoReadiness();

    const startPolling = () => {
      pollingIntervalRef.current = setInterval(() => {
        if (pollingAttempts >= MAX_POLLING_ATTEMPTS) {
          setIsGenerating(false);
          clearInterval(pollingIntervalRef.current);
          return;
        }

        checkVideoReadiness();
      }, POLLING_INTERVAL);
    };

    const pollingTimeout = setTimeout(startPolling, 1000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      clearTimeout(pollingTimeout);
    };
  }, [videoUrl, checkVideoReadiness, pollingAttempts, isNsfw]);

  return {
    isGenerating,
    isInitialCheckComplete,
    videoCheckRef
  };
}; 
